/**
 * ClientCombobox — Sélecteur client dans le formulaire devis/facture.
 *
 * Autocomplete sur nom/email/SIRET, avec option "+ Créer un nouveau client"
 * qui ouvre le ClientEditorSheet inline. Une fois créé/sélectionné,
 * remonte le blob client complet via `onSelect(client)`.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Search, Plus, User, ChevronDown, X, Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ClientEditorSheet from "@/components/ClientEditorSheet";

export default function ClientCombobox({
  value,          // { id, name, ... } | null
  onSelect,       // (client) => void
  onClear,        // () => void
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/clients");
        setAll(data.clients || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return all.slice(0, 30);
    return all.filter((c) => {
      return (
        (c.name || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s) ||
        (c.siret || "").includes(s) ||
        (c.siren || "").includes(s)
      );
    }).slice(0, 30);
  }, [q, all]);

  const handleSelect = (c) => {
    onSelect?.(c);
    setOpen(false);
  };

  const handleCreated = (c) => {
    setAll((a) => [c, ...a]);
    onSelect?.(c);
    setEditorOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              data-testid="client-combobox-trigger"
              className={cn(
                "flex-1 flex items-center gap-2 rounded-md border border-slate-200 bg-white",
                "px-3 py-2 text-sm text-slate-800 hover:border-slate-300 transition-colors",
                "disabled:bg-slate-50 disabled:cursor-not-allowed"
              )}>
              <User className="h-4 w-4 text-slate-400 shrink-0" />
              {value?.name ? (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="truncate font-medium">{value.name}</span>
                  {value.siret && (
                    <span className="font-mono text-[10px] text-slate-400 shrink-0">{value.siret}</span>
                  )}
                </div>
              ) : (
                <span className="flex-1 text-slate-400">Sélectionner un client…</span>
              )}
              <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[420px]" align="start" data-testid="client-combobox-popover">
            <div className="border-b border-slate-200 p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Nom, email, SIRET…"
                  className="pl-8 h-9 bg-white border-slate-200"
                  data-testid="client-combobox-search" />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading && <div className="p-4 text-sm text-slate-500">Chargement…</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-4 text-sm text-slate-500">Aucun client — créez-en un nouveau.</div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelect(c)}
                  data-testid={`client-combobox-item-${c.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2">
                      {c.email && <span className="truncate">{c.email}</span>}
                      {c.siret && <span className="font-mono">{c.siret}</span>}
                      {c.is_recurring && (
                        <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase bg-amber-50 text-amber-700 border-amber-200">
                          récurrent
                        </Badge>
                      )}
                    </div>
                  </div>
                  {value?.id === c.id && <Check className="h-4 w-4 text-blue-600" />}
                </button>
              ))}
            </div>
            <div className="border-t border-slate-200 p-2">
              <Button
                onClick={() => { setOpen(false); setEditorOpen(true); }}
                variant="ghost"
                className="w-full justify-start text-emerald-700 hover:bg-emerald-50 h-8"
                data-testid="client-combobox-create-btn">
                <Plus className="h-4 w-4 mr-2" />
                Créer un nouveau client
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {value && !disabled && (
          <button
            type="button"
            onClick={onClear}
            className="text-slate-400 hover:text-red-600 p-1"
            data-testid="client-combobox-clear-btn"
            title="Retirer le client">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <ClientEditorSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        onSaved={handleCreated}
      />
    </>
  );
}
